import { useState } from 'react';
import {
  CreateClientAccountReceiveOnlyRequest,
  CreateClientAccountStandardRequest,
  EditClientAccountRequest,
  ClientAccount,
  ClientAccountListRequest,
  PaginationMetaData,
} from 'vopay';
import { useApi } from '#shared/providers/Api';

type CreateClientAccountRequest = CreateClientAccountStandardRequest | CreateClientAccountReceiveOnlyRequest;

// Moved from client-dashboard's hooks/useApi/useClientAccounts (ID-13350) so shared/ClientAccountList and
// the host apps consume one hook. Rewired to the host-injected api via useApi(); the return shape is
// unchanged so the client re-export shim keeps every existing caller working. Loading UI is raised by the
// consuming component (loading-conventions), never here.
export default function useClientAccounts() {
  const api = useApi();
  const [pending, setPending] = useState(false);
  const [clientAccount, setClientAccount] = useState<ClientAccount | null>(null);
  const [clientAccounts, setClientAccounts] = useState<ClientAccount[]>([]);

  const [pagination, setPagination] = useState<PaginationMetaData>({
    TotalRecords: 0,
    Limit: 0,
    Offset: 0,
  });

  async function createClientAccount(req: CreateClientAccountRequest, receiveOnly: boolean) {
    try {
      setPending(true);

      const res = await (receiveOnly ? api.createClientAccountReceiveOnly(req) : api.createClientAccountStandard(req));

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return {
        clientAccountID: res.ClientAccountID,
      };
    } finally {
      setPending(false);
    }
  }

  async function fetchClientAccount(clientAccountID: string) {
    try {
      setPending(true);

      const res = await api.fetchClientAccountList({
        ClientAccountID: clientAccountID,
      });

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      const clientAccount = res.ClientAccounts.find((a) => a.ClientAccountID === clientAccountID) || null;
      setClientAccount(clientAccount);

      return clientAccount;
    } finally {
      setPending(false);
    }
  }

  async function fetchClientAccounts(params: ClientAccountListRequest) {
    try {
      setPending(true);
      const res = await api.fetchClientAccountList(params);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      setClientAccounts(res.ClientAccounts);
      setPagination(res.PaginationMetaData);

      return res;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setPending(false);
    }
  }

  async function editClientAccount(params: EditClientAccountRequest) {
    try {
      setPending(true);
      const res = await api.editClientAccount(params);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return res;
    } finally {
      setPending(false);
    }
  }

  return {
    pending,
    createClientAccount,
    clientAccount,
    fetchClientAccount,
    clientAccounts,
    fetchClientAccounts,
    editClientAccount,
    pagination,
  };
}
